/*
 * -----------------------------------------------------------------------
 *  <copyright file="add-lov.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogActions, DialogContent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import { Checkbox } from '@progress/kendo-react-inputs';
import { flatten } from 'lodash';

import { countStudyRecordsAndStudies, LovUsageCounts } from './add-lov.helper';
import { useBoolean, useTranslation } from 'hooks';
import { GET_NAMED_ITEMS, SAVE_NAMED_ITEM } from 'queries/common';
import { getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk } from 'queries/generated/legacy-risk-assessment.generated';
import { NamedItemType } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import { convertNamedType } from 'utils/convertor';
import { validateFields } from 'utils/validation';
import ConfirmationLovDialog from 'views/components/admin/organisations/lovs/confirmation-lov-dialog/confirmation-lov-dialog';
import { SaveButton } from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogTitle } from 'views/components/core/dialog';
import DialogInformationBox from 'views/components/core/dialog-information-box/dialog-information-box';
import Loading from 'views/components/core/loading';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import SelectWithCategories from 'views/components/core/select-with-categories';
import TextField from 'views/components/core/text-field';
import { NamedItemFieldsLOV } from '@queries/generated/fragments.generated';
import { risksAndObservationsVendorIds, risksQualityIssueIds } from '@queries/generated/risk-assessment.generated';
import {
    GET_LIBRARY_RISKS,
    GET_RISK_OBSERVATIONS_AND_ACTIONS_AND_COMPLETE_RISKS,
    ORGANISATION_LIBRARY_CONTROLS_LOV_USAGE,
    ORGANISATION_LIBRARY_FUNCTIONAL_DATA_REVIEWS_LOV_USAGE,
    ORGANISATION_LIBRARY_RISKS_LOV_USAGE,
} from '@queries/legacy-risk-assessment';
import {
    GET_ORGANISATION_CRITICAL_VARIABLES,
    ORGANISATION_CONTROLS_LOV_USAGE,
    ORGANISATION_RISKS_LOV_USAGE,
    RISKS_AND_OBSERVATIONS_VENDOR_IDS,
    RISKS_QUALITY_ISSUE_IDS,
} from '@queries/risk-assessment';
import ReviewLogApi from '@apis/bff-api/central-monitoring/review-logs';

const namedTypes = [
    NamedItemType.RISK_CONTROL_STRATEGY,
    NamedItemType.CONTROL_TYPE,
    NamedItemType.RISK_OBSERVATION_CLASSIFICATION,
    NamedItemType.RISK_CATEGORY,
    NamedItemType.RISK_LIBRARY_CATEGORY,
    NamedItemType.RESPONSIBLE_FUNCTION,
    NamedItemType.REVIEW_LOG_DECISION,
    NamedItemType.VENDOR,
    NamedItemType.CTQEDC_ENTRY,
    NamedItemType.CTQ_TYPE,
    NamedItemType.CONTROLS_REVIEW_LOG_DECISION,
    NamedItemType.CONTROL_PHASE,
    NamedItemType.RISK_QUALITY_ISSUE,
    NamedItemType.SM_REVIEW_LOG_DECISION,
];

const AddLOV = ({
    studyId,
    open,
    lov,
    lovs,
    onClose,
    organisationId,
    studyFunctionIds,
}: {
    studyId: string;
    open: boolean;
    lov: NamedItemFieldsLOV;
    lovs: NamedItemFieldsLOV[];
    onClose: Function;
    organisationId: string;
    studyFunctionIds: any;
}) => {
    const initialState = {
        active: false,
        type: Constants.ObjectId.Empty,
        value: '',
        comments: '',
    };
    const { t } = useTranslation();
    const [saveNamedItem] = useMutation(SAVE_NAMED_ITEM);
    const confirmationOpen = useBoolean(false);
    const [state, setState] = useState({ ...initialState });
    const [errors, setErrors] = useState({} as any);

    const disabledSave = useBoolean(true);
    const [observationsCount, setObservationsCount] = useState(0 as number);
    const [usageCounts, setUsageCounts] = useState<LovUsageCounts | null>(null);
    const [getRiskObservations, { loading, error, data }] = useLazyQuery(
        GET_RISK_OBSERVATIONS_AND_ACTIONS_AND_COMPLETE_RISKS,
    );
    const [getLibraryRisks, { loading: loadingRisks, data: libraryRiskData }] = useLazyQuery(GET_LIBRARY_RISKS);
    const [getOrganisationRisksUsage, { loading: loadingOrgRisksUsage, data: orgRisksUsageData }] =
        useLazyQuery(ORGANISATION_RISKS_LOV_USAGE);
    const [getOrganisationControlsUsage, { loading: loadingOrgControlsUsage, data: orgControlsUsageData }] =
        useLazyQuery(ORGANISATION_CONTROLS_LOV_USAGE);
    const [getOrganisationLibraryRisksUsage, { loading: loadingOrgLibraryRisksUsage, data: orgLibraryRisksUsageData }] =
        useLazyQuery(ORGANISATION_LIBRARY_RISKS_LOV_USAGE);
    const [
        getOrganisationLibraryControlsUsage,
        { loading: loadingOrgLibraryControlsUsage, data: orgLibraryControlsUsageData },
    ] = useLazyQuery(ORGANISATION_LIBRARY_CONTROLS_LOV_USAGE);
    const [
        getOrganisationLibraryFunctionalDataReviewsUsage,
        { loading: loadingOrgLibraryFunctionalDataReviewsUsage, data: orgLibraryFunctionalDataReviewsUsageData },
    ] = useLazyQuery(ORGANISATION_LIBRARY_FUNCTIONAL_DATA_REVIEWS_LOV_USAGE);
    const [usageCheckFailed, setUsageCheckFailed] = useState(false);
    const { data: decisionUsed, isLoading: decisionUsedLoading } = ReviewLogApi.useGetDecisionLOVIsUsed(
        lov?.id as string,
        lov?.type === NamedItemType.REVIEW_LOG_DECISION ||
            lov?.type === NamedItemType.CONTROLS_REVIEW_LOG_DECISION ||
            lov?.type === NamedItemType.SM_REVIEW_LOG_DECISION,
    );

    const [
        getOrganisationCriticalVariables,
        { loading: loadingOrganisationCriticalVariables, data: organisationCriticalVariablesData },
    ] = useLazyQuery(GET_ORGANISATION_CRITICAL_VARIABLES);

    const [
        getRisksObservationsVendorIds,
        { loading: loadingRisksObservationsVendorIdsData, data: risksObservationsVendorIdsData },
    ] = useLazyQuery<risksAndObservationsVendorIds>(RISKS_AND_OBSERVATIONS_VENDOR_IDS);

    const [getRisksQualityIssueIds, { loading: loadingRisksQualityIssueIdsData, data: risksQualityIssueIdsData }] =
        useLazyQuery<risksQualityIssueIds>(RISKS_QUALITY_ISSUE_IDS);

    const getNamedTypesOptions = () => {
        const options = namedTypes.map((item: string) => ({
            id: item,
            text: convertNamedType(item),
        }));

        options.unshift({
            id: Constants.ObjectId.Empty,
            text: t('ra:selectValue'),
        });

        return options;
    };

    const namedTypesOptions = useMemo(getNamedTypesOptions, []);

    const getDataByType = (type: string): any => {
        setUsageCheckFailed(false);
        switch (type) {
            case NamedItemType.CONTROL_PHASE:
            case NamedItemType.CONTROL_TYPE:
                getOrganisationControlsUsage().catch(() => setUsageCheckFailed(true));
                getOrganisationLibraryControlsUsage({ variables: { organisationId } }).catch(() =>
                    setUsageCheckFailed(true),
                );
                break;
            case NamedItemType.RISK_LIBRARY_CATEGORY:
                getLibraryRisks({
                    variables: {
                        organisationId: organisationId ?? Constants.ObjectId.Empty,
                        includeSystemRisks: true,
                    },
                });
                break;
            case NamedItemType.RESPONSIBLE_FUNCTION:
                getOrganisationLibraryRisksUsage({ variables: { organisationId } }).catch(() =>
                    setUsageCheckFailed(true),
                );
                getOrganisationLibraryControlsUsage({ variables: { organisationId } }).catch(() =>
                    setUsageCheckFailed(true),
                );
                getOrganisationLibraryFunctionalDataReviewsUsage().catch(() => setUsageCheckFailed(true));
                break;
            case NamedItemType.CTQ_TYPE:
            case NamedItemType.CTQEDC_ENTRY:
            case NamedItemType.VENDOR:
                getOrganisationCriticalVariables();
                getRisksObservationsVendorIds();
                break;
            case NamedItemType.RISK_QUALITY_ISSUE:
                getRisksQualityIssueIds();
                break;
            default:
                if (
                    type === NamedItemType.RISK_CATEGORY ||
                    type === NamedItemType.RISK_OBSERVATION_CLASSIFICATION ||
                    type === NamedItemType.RISK_CONTROL_STRATEGY
                ) {
                    getOrganisationRisksUsage().catch(() => setUsageCheckFailed(true));
                    getOrganisationLibraryRisksUsage({ variables: { organisationId } }).catch(() =>
                        setUsageCheckFailed(true),
                    );
                } else if (
                    type === NamedItemType.REVIEW_LOG_DECISION ||
                    type === NamedItemType.SM_REVIEW_LOG_DECISION
                ) {
                    getRiskObservations({
                        variables: {
                            studyId: studyId ?? null,
                        },
                    });
                }
                break;
        }
    };

    const checkAssociationByType = (type: NamedItemType): LovUsageCounts | number => {
        switch (type) {
            case NamedItemType.CONTROL_TYPE: {
                const orgLibraryCount =
                    orgLibraryControlsUsageData?.libraryControls.filter(
                        (control: any) => control.controlTypeId === lov?.id,
                    )?.length ?? 0;
                const matchingStudyControls =
                    orgControlsUsageData?.organisationControls.filter((control: any) => control.typeId === lov?.id) ??
                    [];
                return { orgLibraryCount, ...countStudyRecordsAndStudies(matchingStudyControls) };
            }
            case NamedItemType.CONTROL_PHASE: {
                const orgLibraryCount =
                    orgLibraryControlsUsageData?.libraryControls.filter(
                        (control: any) => control.controlPhaseId === lov?.id,
                    )?.length ?? 0;
                const matchingStudyControls =
                    orgControlsUsageData?.organisationControls.filter(
                        (control: any) => control.controlPhaseId === lov?.id,
                    ) ?? [];
                return { orgLibraryCount, ...countStudyRecordsAndStudies(matchingStudyControls) };
            }
            case NamedItemType.RISK_CATEGORY: {
                const orgLibraryCount =
                    orgLibraryRisksUsageData?.libraryRisks.filter((risk: any) => risk.riskCategoryId === lov?.id)
                        ?.length ?? 0;
                const matchingRisks =
                    orgRisksUsageData?.organisationRisks.filter((risk: any) => risk.categoryId === lov?.id) ?? [];
                return { orgLibraryCount, ...countStudyRecordsAndStudies(matchingRisks) };
            }
            case NamedItemType.RISK_OBSERVATION_CLASSIFICATION: {
                const matchingObservations = flatten(
                    orgRisksUsageData?.organisationRisks.map((risk: any) =>
                        (risk.observations ?? [])
                            .filter((obs: any) => obs.classificationId === lov?.id)
                            .map(() => ({ studyId: risk.studyId })),
                    ) ?? [],
                );
                return { orgLibraryCount: 0, ...countStudyRecordsAndStudies(matchingObservations) };
            }
            case NamedItemType.RISK_CONTROL_STRATEGY: {
                const orgLibraryCount =
                    orgLibraryRisksUsageData?.libraryRisks.filter((risk: any) => risk.controlStrategyId === lov?.id)
                        ?.length ?? 0;
                const matchingRisks =
                    orgRisksUsageData?.organisationRisks.filter((risk: any) => risk.controlStrategyId === lov?.id) ??
                    [];
                return { orgLibraryCount, ...countStudyRecordsAndStudies(matchingRisks) };
            }
            case NamedItemType.RESPONSIBLE_FUNCTION: {
                const orgLibraryCount =
                    (orgLibraryRisksUsageData?.libraryRisks.filter(
                        (risk: any) => risk.responsibleFunctionId === lov?.id,
                    )?.length ?? 0) +
                    (orgLibraryControlsUsageData?.libraryControls.filter(
                        (control: any) => control.responsibleFunctionId === lov?.id,
                    )?.length ?? 0) +
                    (orgLibraryFunctionalDataReviewsUsageData?.libraryFunctionalDataReviews.filter(
                        (review: any) => review.responsibleFunctionId === lov?.id,
                    )?.length ?? 0);
                return { orgLibraryCount, studyRecordCount: 0, studyCount: 0 };
            }
            case NamedItemType.RISK_LIBRARY_CATEGORY:
                const libraryRisks =
                    libraryRiskData?.libraryRisks.filter(
                        (libraryRisk: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk) =>
                            libraryRisk != null &&
                            (libraryRisk.riskLibraryCategoryIds?.length ?? 0) > 0 &&
                            lov?.id != null &&
                            libraryRisk.riskLibraryCategoryIds!.includes(lov.id),
                    ) ?? [];

                return libraryRisks?.length;
            case NamedItemType.RISK_QUALITY_ISSUE:
                const risksWithQualityIssue = risksQualityIssueIdsData?.organisationRisks?.filter(
                    (risk) => risk?.qualityIssue?.id === lov?.id,
                );
                return risksWithQualityIssue?.length ?? 0;
            case NamedItemType.CTQ_TYPE:
            case NamedItemType.CTQEDC_ENTRY:
            case NamedItemType.VENDOR:
                const organisationCriticalVariables =
                    organisationCriticalVariablesData?.organisationCriticalVariables?.filter((cv: any) => {
                        if (type === NamedItemType.CTQ_TYPE) return cv.type?.id === lov?.id;
                        if (type === NamedItemType.CTQEDC_ENTRY) return cv.edcEntry?.id === lov?.id;
                        if (type === NamedItemType.VENDOR) return cv.vendors?.some((vendor) => vendor?.id === lov?.id);
                        return false;
                    });
                const organisationRisks =
                    type === NamedItemType.VENDOR
                        ? (risksObservationsVendorIdsData?.organisationRisks?.filter((risk) =>
                              risk?.vendorIds?.some((vendorId) => vendorId === lov?.id),
                          ).length ?? 0)
                        : 0;

                const obs =
                    type === NamedItemType.VENDOR
                        ? flatten(
                              risksObservationsVendorIdsData?.organisationRisks?.map(
                                  (item) => item && item.observations,
                              ) ?? [],
                          )
                        : [];
                const obsCount = obs.filter(
                    (observation) => observation && observation?.vendorIds?.some((vendorId) => vendorId === lov?.id),
                ).length;
                return organisationRisks + obsCount + (organisationCriticalVariables?.length ?? 0);
            case NamedItemType.REVIEW_LOG_DECISION:
            case NamedItemType.CONTROLS_REVIEW_LOG_DECISION:
            case NamedItemType.SM_REVIEW_LOG_DECISION:
                return decisionUsed ? 1 : 0;
            default:
                return 0;
        }
    };

    useEffect(() => {
        if (open && lov && studyId && organisationId) getDataByType(lov.type);
    }, [open, lov, studyId, organisationId]);

    useEffect(() => {
        if (namedTypesOptions.length && !state.type)
            setState({
                ...state,
                type: namedTypesOptions[0].id,
            });
    }, [namedTypesOptions]);

    useEffect(() => {
        if (
            lov &&
            open &&
            (data ||
                libraryRiskData ||
                orgRisksUsageData ||
                orgControlsUsageData ||
                orgLibraryRisksUsageData ||
                orgLibraryControlsUsageData ||
                orgLibraryFunctionalDataReviewsUsageData ||
                organisationCriticalVariablesData ||
                risksObservationsVendorIdsData ||
                risksQualityIssueIdsData ||
                decisionUsed != null ||
                lov.type === NamedItemType.SM_REVIEW_TYPE)
        ) {
            const result = checkAssociationByType(lov?.type);
            if (typeof result === 'number') {
                setObservationsCount(result);
                setUsageCounts(null);
            } else {
                setObservationsCount(result.orgLibraryCount + result.studyRecordCount);
                setUsageCounts(result);
            }

            setState({
                active: lov?.active ?? state.active,
                type: lov?.type ?? state.type,
                value: lov?.name ?? state.value,
                comments: lov?.comments ?? state.comments,
            });
        }
    }, [
        lov,
        open,
        data,
        libraryRiskData,
        orgRisksUsageData,
        orgControlsUsageData,
        orgLibraryRisksUsageData,
        orgLibraryControlsUsageData,
        orgLibraryFunctionalDataReviewsUsageData,
        organisationCriticalVariablesData,
        risksObservationsVendorIdsData,
        risksQualityIssueIdsData,
        decisionUsed,
    ]);

    const close = (action?: string) => {
        if (action !== 'error') {
            setState({ ...initialState });
            setErrors({});
            disabledSave.setTrue();
        }
        onClose(action);
    };

    const sendData = (active?: boolean) => {
        saveNamedItem({
            variables: {
                id: lov?.id ?? null,
                type: state.type,
                name: state.value,
                comments: state.comments,
                active: active || state.active,
                studyId: Constants.ObjectId.Empty,
            },
            refetchQueries: [
                {
                    query: GET_NAMED_ITEMS,
                },
            ],
        })
            .then((result: any) => {
                close(!result?.data?.saveNamedItem ? 'error' : lov ? 'edit' : 'add');
            })
            .catch(() => {
                disabledSave.setFalse();
                close('error');
            });
    };

    const submit = () => {
        const errorValue = validateFields(state.value, 'value', 70, true);
        const typeError =
            state.type === Constants.ObjectId.Empty ? { type: t('ra:requiredFieldError') } : { type: null };
        if (typeError.type || errorValue.value) {
            setErrors({
                ...errors,
                ...typeError,
                ...errorValue,
            });
            return;
        }
        disabledSave.setTrue();

        const hasErrors = Object.values(errors).filter((item) => item !== null).length > 0;

        if (!hasErrors) {
            if (!state.active && lov && (observationsCount > 0 || usageCheckFailed)) confirmationOpen.setTrue();
            else sendData();
        }
    };

    const confirmationOnClose = () => {
        confirmationOpen.setFalse();
    };

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        const error = validateFields(value, field, length, required);

        if (field === 'value' && !error.value) {
            if (
                !lov &&
                value.length &&
                lovs.find(
                    (item: any) =>
                        item.name.toLowerCase().trim() === value.toLowerCase().trim() && item.type === state.type,
                )
            )
                setErrors({
                    ...errors,
                    value: t('admin:LOVduplicateExists'),
                });
            else
                setErrors({
                    ...errors,
                    value: null,
                });
        } else if (field === 'type') {
            if (
                !lov &&
                state.value.length &&
                lovs.find(
                    (item: any) =>
                        item.name.toLowerCase().trim() === state.value.toLowerCase().trim() && item.type === value,
                )
            )
                setErrors({
                    ...errors,
                    ...error,
                    value: t('admin:LOVduplicateExists'),
                });
            else
                setErrors({
                    ...errors,
                    ...error,
                    value: errors.value === t('admin:LOVduplicateExists') ? null : errors.value,
                });
        } else
            setErrors({
                ...errors,
                ...error,
            });
    };

    const onChange = (name: string) => (event: any) => {
        disabledSave.setFalse();
        let length = 70;
        if (name === 'comments') length = 250;

        setState({
            ...state,
            [name]: event.target.value,
        });
        validate(event.target.value, name, length, false);
    };

    if (
        loading ||
        loadingRisks ||
        loadingOrgRisksUsage ||
        loadingOrgControlsUsage ||
        loadingOrgLibraryRisksUsage ||
        loadingOrgLibraryControlsUsage ||
        loadingOrgLibraryFunctionalDataReviewsUsage ||
        loadingOrganisationCriticalVariables ||
        loadingRisksObservationsVendorIdsData ||
        loadingRisksQualityIssueIdsData ||
        decisionUsedLoading
    )
        return <Loading float />;

    return (
        <>
            <ConfirmationLovDialog
                open={confirmationOpen.value}
                close={confirmationOnClose}
                observationsCount={observationsCount}
                usageCounts={usageCounts}
                usageCheckFailed={usageCheckFailed}
                inactivate={(value: boolean) => {
                    confirmationOpen.setFalse();
                    if (state.comments !== lov?.comments || value) sendData(!value);
                    else close();
                }}
                lovType={lov?.type}
            />
            <CustomDialog onClose={close} fullWidth maxWidth="md" open={open}>
                <DialogTitle id="edit-program-title">
                    {lov ? `${t('admin:editLOV')}: ${lov?.recordId || lov?.id}` : t('admin:addLOV')}
                    <FontAwesomeIcon
                        icon={['fal', 'times']}
                        className="fa-lg close-icon-cv"
                        onClick={() => {
                            close();
                        }}
                        data-testid="close-dialog"
                    />
                </DialogTitle>
                <DialogContent style={{ paddingTop: 20, paddingLeft: 20, paddingRight: 20 }}>
                    <Checkbox
                        rounded="none"
                        id="lov-is-active"
                        className="cell-checkbox flex-start no-border no-full-width"
                        checked={state.active}
                        onChange={() => {
                            disabledSave.setFalse();
                            setState({
                                ...state,
                                active: !state.active,
                            });
                        }}
                        label={t('active')}
                        data-testid="lov-is-active"
                        size="medium"
                    />
                    {state.type && state.type !== Constants.ObjectId.Empty ? (
                        <DialogInformationBox topText={t(`admin:namedTypeInfo-${state.type}`)} />
                    ) : null}
                    {!lov ? (
                        <div className="type-edc-wrapper mt no-mb">
                            <FormControl variant="outlined" className="form-control-type mr mt-8">
                                <SelectWithCategories
                                    showWarning={false}
                                    customClassName="form-action-type type"
                                    fieldSelect="type"
                                    onChange={onChange}
                                    label={`${t('admin:listName')}*`}
                                    filterable={false}
                                    searchable
                                    labelField="type"
                                    dataArray={namedTypesOptions}
                                    customId="select-outlined-type"
                                    currentState={state.type}
                                    error={errors.type}
                                />
                            </FormControl>
                            <FormControl variant="outlined" className="form-control-type mt-8">
                                <TextField
                                    id="edit-lov-value"
                                    autoFocus
                                    label={`${t('value')}*`}
                                    notched
                                    fullWidth
                                    value={state.value}
                                    onChange={onChange('value')}
                                    size="small"
                                    placeholder={t('enterValue')}
                                    helperText={
                                        <CharactersRemaining
                                            id="edit-lov-value-characters"
                                            length={state.value.length}
                                            max={70}
                                            error={errors.value}
                                        />
                                    }
                                    variant="outlined"
                                    color="secondary"
                                    error={!!errors.value}
                                />
                            </FormControl>
                        </div>
                    ) : null}
                    {lov ? (
                        <>
                            <div className="content-field modal pt">
                                <p className="title">{t('admin:listName')}:</p>
                                {convertNamedType(state.type)}
                            </div>
                            <div className="content-field modal pb">
                                <p className="title">{t('value')}:</p>
                                {state.value}
                            </div>
                        </>
                    ) : null}
                    <TextField
                        id="edit-lov-comments"
                        autoFocus
                        label={t('ra:comments')}
                        notched
                        fullWidth
                        multiline
                        rows={4}
                        value={state.comments}
                        onChange={onChange('comments')}
                        size="small"
                        placeholder={t('ra:enterComments')}
                        helperText={
                            <CharactersRemaining
                                id="edit-lov-comments-characters"
                                length={state.comments.length}
                                max={250}
                                error={errors.comments}
                            />
                        }
                        variant="outlined"
                        color="secondary"
                        className="mb"
                        error={!!errors.comments}
                    />
                </DialogContent>
                <DialogActions
                    className={`actions-info-wrapper-edit-cv actions-add-risk assign-items ${!lov ? 'flex-end' : ''}`}
                >
                    <ModalUpdatedInfo item={lov} />
                    <SaveButton
                        className="edit-cv-update-btn small"
                        id="edit-lov-save"
                        text={lov ? 'common:update' : 'common:add'}
                        disabled={Object.keys(errors).filter((item) => errors[item]).length > 0 || disabledSave.value}
                        onClick={submit}
                    />
                </DialogActions>
            </CustomDialog>
        </>
    );
};

export default AddLOV;
